import { useEffect, useState } from 'react';
import { api, type ApiError } from '../api/client';
import { Alert } from './Field';
import { RichText } from './RichText';

export interface Preview {
  id: string;
  title: string;
  type: string;
  courseTitle: string;
  body?: string;
  language?: string;
  streamUrl?: string;
  mimeType?: string;
  externalUrl?: string;
}

/**
 * Plays a free-preview lesson.
 *
 * The API decides what a visitor may see: this component only renders whatever
 * the preview endpoint returns, and a lesson that is not marked as preview
 * simply 404s.
 */
export function PreviewModal({ lessonId, onClose }: { lessonId: string; onClose: () => void }) {
  const [preview, setPreview] = useState<Preview | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    api<Preview>(`/lessons/${lessonId}/preview`)
      .then(setPreview)
      .catch((caught) => setError(caught as ApiError));
  }, [lessonId]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="modal__backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="modal" onClick={(event) => event.stopPropagation()}>
        <div className="modal__head">
          <span className="modal__title">{preview?.title ?? 'Preview'}</span>
          <button className="modal__close" onClick={onClose} aria-label="Close preview">
            ×
          </button>
        </div>

        <div className="modal__body">
          {error ? <Alert kind="error" title={error.message} /> : null}
          {!preview && !error ? <div className="spinner">Loading preview…</div> : null}
          {preview ? <PreviewBody preview={preview} /> : null}
        </div>
      </div>
    </div>
  );
}

/** Shared with the review queue, so a reviewer sees content as a learner will. */
export function PreviewBody({ preview }: { preview: Preview }) {
  if (preview.body && preview.type === 'CODE') {
    // Source, not markup: shown verbatim rather than interpreted as HTML.
    return (
      <div className="prose">
        <pre>
          <code>{preview.body}</code>
        </pre>
      </div>
    );
  }

  if (preview.body) {
    // Already sanitised server-side on save, and re-sanitised on every edit.
    return <RichText html={preview.body} />;
  }

  if (preview.streamUrl) {
    const mime = preview.mimeType ?? '';
    if (mime.startsWith('video/')) {
      return <video src={preview.streamUrl} controls style={{ width: '100%' }} />;
    }
    if (mime.startsWith('image/')) {
      return <img src={preview.streamUrl} alt={preview.title} />;
    }
    return (
      <>
        <p className="muted" style={{ marginTop: 0 }}>
          This preview is a {mime || 'file'}.
        </p>
        <iframe
          src={preview.streamUrl}
          title={preview.title}
          style={{ width: '100%', height: '60vh', border: 0 }}
        />
      </>
    );
  }

  if (preview.externalUrl) {
    return (
      <p>
        This lesson links out to{' '}
        <a href={preview.externalUrl} target="_blank" rel="noopener noreferrer">
          {preview.externalUrl}
        </a>
        .
      </p>
    );
  }

  return <p className="muted">Nothing to preview.</p>;
}
